import type { Fragment, Node as PMNode } from "@tiptap/pm/model";

// The document as LaTeX source: the formulas as they are, the text around them escaped, and the
// structure (headings, lists, tables) in its plain LaTeX form. For a student who wants to paste the
// working somewhere that takes LaTeX - or into our answer editor as text.

const SPECIALS: Record<string, string> = {
  "\\": "\\textbackslash{}", "{": "\\{", "}": "\\}", "#": "\\#", "%": "\\%",
  "&": "\\&", "_": "\\_", "^": "\\^{}", "~": "\\~{}", "$": "\\$",
};

function escapeText(text: string): string {
  return text.replace(/[\\{}#%&_^~$]/g, (c) => SPECIALS[c]);
}

function inline(node: PMNode): string {
  let out = "";
  node.forEach((child) => {
    if (child.type.name === "liveMath") out += `$${child.attrs.latex}$`;
    else if (child.type.name === "hardBreak") out += "\\\\\n";
    else if (child.isText) {
      let text = escapeText(child.text || "");
      for (const mark of child.marks) {
        if (mark.type.name === "bold") text = `\\textbf{${text}}`;
        else if (mark.type.name === "italic") text = `\\textit{${text}}`;
        else if (mark.type.name === "underline") text = `\\underline{${text}}`;
      }
      out += text;
    }
  });
  return out;
}

function block(node: PMNode): string {
  switch (node.type.name) {
    case "paragraph":
      return inline(node);
    case "heading": {
      const command = ["section", "subsection", "subsubsection"][Math.min(node.attrs.level, 3) - 1];
      return `\\${command}*{${inline(node)}}`;
    }
    case "liveMathDisplay":
      return `\\[\n${node.attrs.latex}\n\\]`;
    case "bulletList":
    case "orderedList": {
      const env = node.type.name === "bulletList" ? "itemize" : "enumerate";
      const items: string[] = [];
      node.forEach((item) => items.push(`  \\item ${blocks(item.content).replace(/\n\n/g, "\n")}`));
      return `\\begin{${env}}\n${items.join("\n")}\n\\end{${env}}`;
    }
    case "blockquote":
      return `\\begin{quote}\n${blocks(node.content)}\n\\end{quote}`;
    case "table": {
      const rows: string[] = [];
      let columns = 0;
      node.forEach((row) => {
        const cells: string[] = [];
        row.forEach((cell) => cells.push(blocks(cell.content).replace(/\n+/g, " ")));
        columns = Math.max(columns, cells.length);
        rows.push(cells.join(" & ") + " \\\\ \\hline");
      });
      return `\\begin{tabular}{|${"c|".repeat(columns)}}\n\\hline\n${rows.join("\n")}\n\\end{tabular}`;
    }
    case "horizontalRule":
      return "\\hrulefill";
    default:
      return node.isTextblock ? inline(node) : blocks(node.content);
  }
}

function blocks(fragment: Fragment): string {
  const parts: string[] = [];
  fragment.forEach((node) => parts.push(block(node)));
  return parts.join("\n\n");
}

/** A document or a slice of one - its content - as LaTeX. */
export function toLatex(fragment: Fragment): string {
  return blocks(fragment).replace(/\n{3,}/g, "\n\n").trim();
}
